import {useMemo} from 'react'
import type {ComponentProps, Key} from 'react'
import {ActionMenu} from '@primer/react-brand/esm'

import {
  renderOSAwareDownloadActionMenu,
  useOSAwareDownloadActionMenu,
  type OSAwareDownloadActionMenuAnalytics,
  type OSAwareDownloadActionMenuController,
  type OSAwareDownloadActionMenuElement,
} from '../../OSAwareDownloadActionMenu'
import type {DownloadOptions} from '../../../schemas/contentful/contentTypes/downloadOptions'
import {
  toOSAwareDownloadActions,
  toResolvedDownloadOptions,
  type ResolvedDownloadOptions,
} from './resolve-download-options'

export type ContentfulDownloadActionMenuProps = {
  component: DownloadOptions
  analytics?: OSAwareDownloadActionMenuAnalytics
  size?: 'small' | 'medium'
  variant?: ComponentProps<typeof ActionMenu.Button>['variant']
}

export type RenderContentfulDownloadActionMenuOptions = {
  component?: DownloadOptions
  resolved?: ResolvedContentfulDownloadOptions
  analytics?: OSAwareDownloadActionMenuAnalytics
  size?: 'small' | 'medium'
  variant?: ComponentProps<typeof ActionMenu.Button>['variant']
  elementKey?: Key
}

export type ContentfulDownloadActionMenuElement = OSAwareDownloadActionMenuElement
export type ResolvedContentfulDownloadOptions = ResolvedDownloadOptions & {
  actionMenuId: string
  controller: OSAwareDownloadActionMenuController
}

/**
 * Resolves OS/architecture-aware download options for `component`, always
 * calling hooks unconditionally so callers can use it even when `component`
 * isn't available yet (e.g. an unresolved Contentful reference). Architecture
 * detection is skipped whenever `component` is absent.
 */
export function useResolvedDownloadOptions(component?: DownloadOptions): ResolvedContentfulDownloadOptions | undefined {
  const actions = useMemo(() => (component ? toOSAwareDownloadActions(component) : undefined), [component])
  const controller = useOSAwareDownloadActionMenu(actions, component?.fields.menuLabel)

  return useMemo(() => {
    if (!component || !controller) return undefined

    return {
      ...toResolvedDownloadOptions(component, controller),
      actionMenuId: controller.actionMenuId,
      controller,
    }
  }, [component, controller])
}

/**
 * Pure render helper whose returned element's `type` is directly Primer
 * Brand's `ActionMenu` (not a wrapper). Primer Brand's `Hero.ButtonGroup` /
 * `CTABanner.ButtonGroup` filter children by exact element type and only keep
 * direct `Button`/`ActionMenu` elements, so this lets Flex Suite parents
 * render a compliant direct child instead of a `ContentfulDownloadActionMenu`
 * wrapper. Returns `null` when `component` or `resolved` is missing.
 */
export function renderContentfulDownloadActionMenu(
  options: RenderContentfulDownloadActionMenuOptions & {
    component: DownloadOptions
    resolved: ResolvedContentfulDownloadOptions
  },
): ContentfulDownloadActionMenuElement
export function renderContentfulDownloadActionMenu(
  options: RenderContentfulDownloadActionMenuOptions,
): ContentfulDownloadActionMenuElement | null
export function renderContentfulDownloadActionMenu({
  component,
  resolved,
  analytics,
  size,
  variant,
  elementKey,
}: RenderContentfulDownloadActionMenuOptions): ContentfulDownloadActionMenuElement | null {
  if (!component || !resolved) return null

  return renderOSAwareDownloadActionMenu({
    controller: resolved.controller,
    menuLabel: component.fields.menuLabel,
    analytics,
    size,
    variant,
    elementKey,
  })
}

/**
 * Standalone wrapper for stories/tests. Flex Suite parents that need a direct
 * `ActionMenu` child (e.g. inside `Hero.ButtonGroup`) should instead compose
 * `useResolvedDownloadOptions` + `renderContentfulDownloadActionMenu`.
 */
export function ContentfulDownloadActionMenu({component, analytics, size, variant}: ContentfulDownloadActionMenuProps) {
  const resolved = useResolvedDownloadOptions(component)

  return renderContentfulDownloadActionMenu({
    component,
    resolved,
    analytics,
    size,
    variant,
  })
}
